import { useMutation, useQuery } from '@apollo/client'
import { formatPrice } from '@commerce/product/use-price'
import type { Product } from '@commerce/types/product'
import {
  getCartId,
  getCurrencyCountryCode,
  getLanguage,
  normalizeCart,
} from '@commerce/utils'
import cartLinesAddMutation, {
  cartLinesAddVars,
} from '@commerce/utils/mutations/cart-lines-add'
import getCartQuery from '@commerce/utils/queries/get-cart-query'
import WishlistButton from '@components/arkade/list/WishlistButton'
import { Price } from '@components/common'
import { useUI } from '@components/ui'
import {
  PREP_COAT_UPSELL_PRODUCTS,
  SAMPLE_GARDEN_POT_UPSELL_PRODUCTS,
  checkUpsellProductExists,
  getUpsellProdutsByProduct,
} from '@lib/cart'
import { pinterestAddToCartEvent } from '@lib/events/addToCart'
import {
  PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY,
  PRODUCT_TYPE_COLOUR,
  PRODUCT_TYPE_COLOUR_CONSULTATION,
  PRODUCT_TYPE_LANDING_PAGE,
  RETAIL_PRICE_CUSTOM_ATTRIBUTE_KEY,
  findBundleInfoFromMetafield,
  findMetafieldValueByKey,
} from '@lib/helpers/general'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import BundleProductOptionsSelectBox from '../BundleProductOptions/BundleProductOptionsSelectBox'
import ProductDetailsPDP from '../ProductDetailsPDP/ProductDetailsPDP'
import ProductOptionsSelectBox from '../ProductOptions/ProductOptionsSelectBox'
import TabbedProductSiblingOptions from '../TabbedProductSiblingOptions'
import { SelectedOptions, getProductVariant } from '../helpers'
import StandardProduct from './StandardProduct'
import ViewColours from './ViewColours'
interface ProductSidebarProps {
  product: Product
  productSiblings: Product[]
  prismicContent: any
  selectedSibling: Product | undefined
  setSelectedSibling: React.Dispatch<React.SetStateAction<Product | undefined>>
}

const ProductSidebar: FC<ProductSidebarProps> = ({
  product,
  productSiblings,
  prismicContent,
  selectedSibling,
  setSelectedSibling,
}) => {
  const { t } = useTranslation(['common'])
  const {
    openCartNotification,
    setSidebarView,
    openSidebar,
    setUpsellViewContent,
    setUpsellProductType,
  } = useUI()
  // const { hits, sendEvent } = useHits()
  const [loading, setLoading] = useState(false)
  const { locale } = useRouter()
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const [bySamplePotSelected, setBySamplePotSelected] = useState(false)
  const bundleInfo = findBundleInfoFromMetafield(product.metafields)
  const isBundleProduct = !!bundleInfo
  const isColourProduct = product.productType === PRODUCT_TYPE_COLOUR
  const isLandingPageProduct = product.productType === PRODUCT_TYPE_LANDING_PAGE
  const isColourConsultation =
    product.productType === PRODUCT_TYPE_COLOUR_CONSULTATION
  const isNoVariantProduct = product.variants.length === 1
  const isProductSiblings = productSiblings?.length > 0
  const descriptionHtml = product?.descriptionHtml

  const cartId = getCartId(locale)
  const { data } = useQuery(getCartQuery, {
    variables: {
      cartId,
      country: getCurrencyCountryCode(locale),
      language: getLanguage(locale),
    },
    context: { locale },
    skip: !cartId,
  })
  const cart = data?.cart ? normalizeCart(data.cart) : null
  const [addItem] = useMutation(cartLinesAddMutation, {
    onCompleted() {
      pinterestAddToCartEvent([
        {
          product_id: selectedSibling?.id || '',
          product_variant:
            selectedOptions?.size || selectedSibling?.variants?.[0]?.name || '',
          product_variant_id:
            selectedSibling?.variants?.find(
              (variant) =>
                variant?.name?.toLowerCase() ===
                selectedOptions?.size?.toString().toLowerCase()
            )?.id ||
            selectedSibling?.variants?.[0]?.id ||
            '',
          product_price:
            selectedSibling?.variants?.find(
              (variant) =>
                variant?.name?.toLowerCase() ===
                selectedOptions?.size?.toString().toLowerCase()
            )?.price ||
            selectedSibling?.variants?.[0]?.price ||
            0,
          product_quantity: 1,
          currency: selectedSibling?.price?.currencyCode || 'AUD',
        },
      ])
      const upsellProducts = getUpsellProdutsByProduct(selectedSibling!)
      const upsellBrushExists = checkUpsellProductExists(cart!, upsellProducts)
      const upsellPrepCoatExists = checkUpsellProductExists(
        cart!,
        PREP_COAT_UPSELL_PRODUCTS
      )
      if (
        !bySamplePotSelected ||
        (upsellBrushExists.length > 0 && upsellPrepCoatExists.length > 0)
      )
        openCartNotification(
          bySamplePotSelected
            ? `${selectedSibling?.variants?.[0]?.name} ${
                selectedSibling?.name
              } ${t('added-to-cart')}`
            : `1 ${selectedSibling?.name} ${t('added-to-cart')}`
        )

      // Show upsell side bar if upsell brush does not exist in the cart
      if (
        bySamplePotSelected &&
        (upsellBrushExists.length === 0 || upsellPrepCoatExists.length === 0)
      ) {
        setUpsellProductType(
          upsellBrushExists.length === 0 ? 'BRUSH' : 'PREP_COAT'
        )
        const upSellProducts = []
        if (upsellBrushExists.length === 0)
          upSellProducts.push(...SAMPLE_GARDEN_POT_UPSELL_PRODUCTS)
        if (upsellPrepCoatExists.length === 0)
          upSellProducts.push(...PREP_COAT_UPSELL_PRODUCTS)
        setUpsellViewContent(
          `${selectedSibling?.variants?.[0]?.name} ${selectedSibling?.name} ${t(
            'added-to-cart'
          )}`,
          upSellProducts
        )
        setSidebarView('UPSELL_VIEW')
        openSidebar()
      }
      setLoading(false)
    },
    onError() {
      setLoading(false)
    },
  })

  const variant = getProductVariant(product, selectedOptions)
  const productUnavailable = product?.tags?.includes('unavailable')
  // addToCart for product siblings who does not need further selection
  const addSampleProductSiblingToCart = async () => {
    setLoading(true)
    const customAttributes = [
      { key: PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY, value: product.handle || '' },
    ]
    const selectedVariant = selectedSibling?.variants[0]
    const recipe = findMetafieldValueByKey(
      selectedVariant?.metafields!,
      'recipe'
    )
    if (selectedSibling?.productType !== 'Colour Card' && recipe) {
      customAttributes.push({
        key: '_recipe',
        value: recipe,
      })
    }
    if (
      selectedVariant?.originalPrice &&
      selectedVariant.originalPrice > selectedVariant.price
    ) {
      customAttributes.push({
        key: RETAIL_PRICE_CUSTOM_ATTRIBUTE_KEY,
        value: formatPrice({
          amount: selectedVariant.originalPrice,
          currencyCode: product.price.currencyCode!,
          locale,
        }),
      })
    }

    addItem({
      variables: cartLinesAddVars({
        locale,
        variantId: String(selectedVariant?.id),
        quantity: 1,
        attributes: customAttributes,
      }),
      context: { locale },
    })
  }
  useEffect(() => {
    setBySamplePotSelected(
      selectedSibling?.handle ===
        findMetafieldValueByKey(product?.metafields!, 'sample_pot')
        ? true
        : false
    )
  }, [selectedSibling])
  const bundleProductVariants =
    bundleInfo &&
    product?.variants.filter((variant) =>
      bundleInfo.some((bundleItem) => bundleItem.variant === variant.id)
    )

  // b2b pricing dispaly
  let selectedOptionPrice = product.price.value
  let selectedOptionListPrice = product?.originalPrice?.value
    ? product.originalPrice.value
    : product.listPrice.value

  if (Object.keys(selectedOptions).length > 0 && variant) {
    selectedOptionPrice = variant.price
    selectedOptionListPrice = variant?.originalPrice
      ? variant.originalPrice
      : variant.listPrice
  }

  return (
    <div className="flex h-full flex-col lg:pb-7 lg:pl-4">
      <div className="flex items-center justify-between">
        <h1 className="lg:heading-2 heading-mobile-2 pt-4 text-ui-dark-grey lg:pt-0">
          {product.name}
        </h1>
        <WishlistButton product={product} isPDP />
      </div>
      {isLandingPageProduct && (
        <span className="body-1-bold pt-4">
          {findMetafieldValueByKey(
            product?.metafields!,
            'landing_page_display_price'
          )}
        </span>
      )}
      {!isColourProduct && !isLandingPageProduct && !isBundleProduct && (
        <div className=" flex items-center gap-2">
          <Price
            price={selectedOptionPrice}
            currencyCode={product.price.currencyCode}
            listPrice={selectedOptionListPrice}
            prefix={
              isNoVariantProduct
                ? false
                : Object.keys(selectedOptions).length !== product.options.length
            }
            className="body-1-bold key py-4"
          />
        </div>
      )}
      {descriptionHtml && (
        <ProductDetailsPDP
          descriptionHtml={descriptionHtml}
          isColourProduct={isColourProduct}
        />
      )}
      {isBundleProduct && bundleProductVariants && (
        <div className="mt-6 h-full">
          <BundleProductOptionsSelectBox
            product={product}
            bundleInfo={bundleInfo}
            currencyCode={product?.price?.currencyCode}
            variants={bundleProductVariants}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            prismicContent={prismicContent}
            disabled={
              productUnavailable ||
              variant?.availableForSale === false ||
              loading ||
              !variant?.id ||
              !selectedOptions ||
              (Object.keys(selectedOptions).length !== product.options.length &&
                !isNoVariantProduct)
            }
          />
        </div>
      )}
      {!isColourProduct &&
        !isProductSiblings &&
        !isBundleProduct &&
        product?.options && (
          <div className="mt-8">
            <ProductOptionsSelectBox
              currencyCode={product?.price?.currencyCode}
              variants={product?.variants}
              options={product?.options}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              colourConsultationProduct={isColourConsultation}
            />
          </div>
        )}
      {isProductSiblings && isColourProduct && (
        <div className="mt-4 mb-8 md:my-10">
          <TabbedProductSiblingOptions
            siblings={productSiblings}
            selectedSibling={selectedSibling}
            setSelectedSibling={setSelectedSibling}
            currencyCode={product.price.currencyCode}
            prismicContent={prismicContent}
            parentProduct={product}
            cart={cart}
            addSampleProductSiblingToCart={addSampleProductSiblingToCart}
            loading={loading}
          />
        </div>
      )}
      <StandardProduct
        selectedOptions={selectedOptions}
        product={product}
        productName={product.name}
        variant={variant || product.variants[0]}
        buttonText={
          variant?.availableForSale === false
            ? 'Not Available'
            : productUnavailable
            ? `${t('unavailable')}`
            : `${t('add-to-cart')}`
        }
        disabled={
          productUnavailable ||
          variant?.availableForSale === false ||
          loading ||
          !variant?.id ||
          !selectedOptions ||
          (Object.keys(selectedOptions).length !== product.options.length &&
            !isNoVariantProduct)
        }
        isStandardProduct={
          !isColourProduct && !isLandingPageProduct && !isBundleProduct
        }
      />
      <ViewColours
        product={product}
        isLandingPageProduct={isLandingPageProduct}
      />
    </div>
  )
}

export default ProductSidebar
